import { useState, useEffect, useRef } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { parseExcelForImport } from "../../utils/excelImport";


export function ExcelImportModal({ parsedShift, existingEntries, onImport, onClose, initialFile }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const [step,        setStep]       = useState("upload");  // upload | preview | importing | done
  const [preview,     setPreview]    = useState([]);
  const [error,       setError]      = useState("");
  const [dropping,    setDropping]   = useState(false);
  const [fileName,    setFileName]   = useState("");
  const [newCount,    setNewCount]   = useState(0);
  const [updatedCount,setUpdatedCount] = useState(0);
  const fileRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;
    setError("");
    setFileName(file.name);
    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_BYTES) {
      setError("File is too large (max 10 MB). Please check you selected the correct file.");
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const rows = parseExcelForImport(buf, parsedShift);
      if (rows.length === 0) {
        setError("No 'At Risk' or 'Unachievable' rows found. Check that column P contains these values.");
        return;
      }
      setPreview(rows);
      setStep("preview");
    } catch (e) {
      setError("Parse error: " + e.message);
    }
  };

  // Auto-process a file dropped onto the app before the modal was opened
  useEffect(() => {
    if (initialFile) processFile(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrop = (e) => {
    e.preventDefault(); setDropping(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleConfirmImport = async () => {
    setStep("importing");
    try {
      const result = await onImport(preview);
      setNewCount(result?.newCount ?? preview.length);
      setUpdatedCount(result?.updatedCount ?? 0);
      setStep("done");
    } catch (e) {
      setError("Import failed: " + e.message);
      setStep("preview");
    }
  };

  // Rows that would replace vs new
  const existingImported = existingEntries.filter(e => e.autoImported).length;

  return (
    <div style={{ ...overlayBackdrop, zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...GLASS.modal,
        borderRadius: 16,
        width: "100%", maxWidth: 560, maxHeight: "90vh",
        display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(0,0,0,0.9)" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, letterSpacing: "0.2em",
              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>
              Import Excel
            </div>
            <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.08em", marginTop: 3,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              {step === "upload"    && "Drop your connections file to import At Risk entries"}
              {step === "preview"   && `${preview.length} entries ready to import`}
              {step === "importing" && "Importing entries..."}
              {step === "done"      && "Import complete"}
            </div>
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

          {step === "upload" && (
            <>
              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDropping(true); }}
                onDragLeave={() => setDropping(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                style={{ border: `2px dashed ${dropping ? SA.lightSilver : SA.borderGray}`,
                  borderRadius: RADIUS.sharp, padding: "40px 24px", textAlign: "center",
                  cursor: "pointer", transition: "border-color 0.2s",
                  background: dropping ? "rgba(255,255,255,0.03)" : "transparent" }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>📂</div>
                <div style={{ fontSize: TYPOGRAPHY.label, color: SA.lightSilver, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                  marginBottom: 8 }}>Drop .xlsx file here</div>
                <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                  or click to browse
                </div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={e => processFile(e.target.files[0])} />
              </div>
              {existingImported > 0 && (
                <div style={{ marginTop: 14, padding: "10px 14px", background: tokens.warningBg,
                  border: `1px solid rgba(200,168,48,0.3)`, borderRadius: 2 }}>
                  <div style={{ fontSize: TYPOGRAPHY.tiny, color: tokens.warning, letterSpacing: "0.1em",
                    fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                    ⚠ {existingImported} previously imported card{existingImported > 1 ? "s" : ""} will be replaced.
                    Manually created or edited cards will be preserved.
                  </div>
                </div>
              )}
              {error && (
                <div style={{ marginTop: 12, fontSize: TYPOGRAPHY.tiny, color: tokens.monoRed,
                  fontFamily: "'Helvetica Neue',Arial,sans-serif", letterSpacing: "0.08em" }}>
                  {error}
                </div>
              )}
            </>
          )}

          {step === "preview" && (
            <>
              {existingImported > 0 && (
                <div style={{ marginBottom: 12, padding: "10px 14px", background: tokens.warningBg,
                  border: `1px solid rgba(200,168,48,0.3)`, borderRadius: 2 }}>
                  <div style={{ fontSize: TYPOGRAPHY.tiny, color: tokens.warning, letterSpacing: "0.1em",
                    fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                    ⚠ {existingImported} previously imported card{existingImported > 1 ? "s" : ""} will be replaced.
                    Manually created cards are safe.
                  </div>
                </div>
              )}
              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                marginBottom: 10, letterSpacing: "0.06em" }}>
                {fileName} · {preview.length} at-risk connections
              </div>
              {/* Preview table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 8,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", color: SA.silver }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${SA.borderGray}` }}>
                      {["Airline","Inbound","Origin","STA","ETA","Outbound","Dest","STD","ETD","PAX","Bags","Section"].map(h => (
                        <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: SA.borderGray,
                          letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((e, i) => (
                      <tr key={`${e.inFlt || ''}-${e.outFlt || ''}-${i}`} style={{ borderBottom: `1px solid ${SA.borderGray}`,
                        background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                        <td style={{ padding: "5px 8px", color: SA.lightSilver }}>{e.airline}</td>
                        <td style={{ padding: "5px 8px" }}>{e.inFlt}</td>
                        <td style={{ padding: "5px 8px" }}>{e.origin}</td>
                        <td style={{ padding: "5px 8px" }}>{e.sta}</td>
                        <td style={{ padding: "5px 8px" }}>{e.eta}</td>
                        <td style={{ padding: "5px 8px" }}>{e.outFlt}</td>
                        <td style={{ padding: "5px 8px" }}>{e.dest}</td>
                        <td style={{ padding: "5px 8px" }}>{e.std}</td>
                        <td style={{ padding: "5px 8px" }}>{e.etd}</td>
                        <td style={{ padding: "5px 8px" }}>{e.ttlPax}</td>
                        <td style={{ padding: "5px 8px" }}>{e.ttlBags}</td>
                        <td style={{ padding: "5px 8px", color: e.section === "outlook" ? SA.borderGray : tokens.monoGreen }}>
                          {e.section === "outlook" ? "Post" : "Active"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === "importing" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.14em",
                textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                Importing {preview.length} entries...
              </div>
            </div>
          )}

          {step === "done" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: TYPOGRAPHY.label, color: tokens.monoGreen, letterSpacing: "0.14em",
                textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                marginBottom: 10 }}>
                Import complete
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 4 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                    color: tokens.monoGreen, fontWeight: 600 }}>{newCount}</div>
                  <div style={{ fontSize: 8, color: SA.silver, letterSpacing: "0.12em",
                    textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                    marginTop: 3 }}>New</div>
                </div>
                {updatedCount > 0 && (
                  <>
                    <div style={{ width: 1, background: SA.borderGray, alignSelf: "stretch" }} />
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                        color: SA.lightSilver, fontWeight: 600 }}>{updatedCount}</div>
                      <div style={{ fontSize: 8, color: SA.silver, letterSpacing: "0.12em",
                        textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                        marginTop: 3 }}>Updated</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(step === "preview" || step === "done") && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${SA.borderGray}`,
            display: "flex", gap: 10 }}>
            {step === "preview" && (
              <>
                <Button onClick={() => setStep("upload")}
                  style={{ flex: 1, padding: "12px", background: "transparent",
                    border: `1px solid ${SA.borderGray}`, borderRadius: 2,
                    color: SA.silver, fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.14em",
                    textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                  ← Back
                </Button>
                <Button onClick={handleConfirmImport}
                  style={{ flex: 2, padding: "12px", background: SA.medGray,
                    border: `1px solid ${SA.lightSilver}`, borderRadius: 2,
                    color: SA.white, fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.14em",
                    textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>
                  Import {preview.length} Entries
                </Button>
              </>
            )}
            {step === "done" && (
              <Button onClick={onClose}
                style={{ flex: 1, padding: "12px", background: SA.medGray,
                  border: `1px solid ${SA.lightSilver}`, borderRadius: 2,
                  color: SA.white, fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>
                Done
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
